import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import { keycloak, updateToken } from './keycloak';

import type { RootState } from '../store';

// Wrap the base query to block requests if Keycloak is not yet authenticated.
// This is a last-resort safety net — the primary guard is AuthProvider blocking
// children from rendering while isLoading is true.
const rawIamBaseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_IAM_API_URL || 'http://localhost:7010',
  prepareHeaders: async (headers, { getState }) => {
    const state = getState() as RootState;
    const orgId = state.org?.selectedOrgId;

    if (orgId) {
      headers.set('X-Tenant-Id', orgId);
    }

    if (keycloak.authenticated && keycloak.token) {
      try {
        await updateToken(30);
        headers.set('Authorization', `Bearer ${keycloak.token}`);
      } catch (error) {
        console.error('[iamApiSlice] Token refresh failed:', error);
      }
    }
    return headers;
  },
});

const iamBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  if (!keycloak.authenticated || !keycloak.token) {
    console.warn(
      '[iamApiSlice] Request blocked — Keycloak not yet authenticated.'
    );
    return {
      error: {
        status: 'CUSTOM_ERROR',
        error: 'Keycloak not yet authenticated',
      } as FetchBaseQueryError,
    };
  }
  return rawIamBaseQuery(args, api, extraOptions);
};

export const iamApiSlice = createApi({
  reducerPath: 'iamApi',
  baseQuery: iamBaseQuery,
  tagTypes: [
    'Organization',
    'SubscriptionPlan',
    'OrgSubscription',
    'Group',
    'Role',
    'DefaultRoleActions',
    'Resource',
    'Action',
    'User',
  ],
  endpoints: () => ({}),
});
